import React, { useState, useEffect } from 'react';


const ProductModal = ({ isOpen, onClose, onSave, initialData, translations, isAddMode }) => {
  const [formData, setFormData] = useState({
    type: 'FERTILIZER',
    name: '',
    description: '',
    storageMethod: '',
    formulation: '',
    controlTarget: '',
    activeIngredient: '',
    activeConcentration: '',
    recommendedDose: '',
    applicationMethod: '',
    benefit: '',
  });
  const [message, setMessage] = useState('');

  const t = translations;

  useEffect(() => {
    if (initialData) {
      setFormData({ ...initialData });
    } else {
      // Default values for new product
      setFormData({
        type: 'FERTILIZER',
        name: '',
        description: '',
        storageMethod: '',
        formulation: '',
        controlTarget: '',
        activeIngredient: '',
        activeConcentration: '',
        recommendedDose: '',
        applicationMethod: '',
        benefit: '',
      });
    }
    setMessage('');
  }, [initialData, isOpen]);

  // Validates required fields
  const validateFields = () => {
    const requiredFields = [
      'type',
      'name',
      'description',
      'storageMethod',
      'formulation',
      'activeIngredient',
      'activeConcentration',
      'recommendedDose',
      'applicationMethod',
    ];

    if (formData.type === 'FERTILIZER') {
      requiredFields.push('benefit');
    }
    if (formData.type === 'PESTICIDE') {
      requiredFields.push('controlTarget');
    }

    const missingFields = requiredFields.filter((field) => !formData[field] || formData[field].trim() === '');
    return missingFields;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setMessage('');

    const missingFields = validateFields();
    if (missingFields.length > 0) {
      setMessage(`${t.missingFields} ${missingFields.map((field) => t[field]).join(', ')}`);
      return;
    }

    onSave(formData);
  };

  if (!isOpen) return null;

  return (
    <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)'}} tabIndex="-1">
      <div className="modal-dialog modal-lg">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">{isAddMode ? t.addProduct : t.productInfo}</h5>
            <button type="button" className="btn-close" onClick={onClose}></button>
          </div>
          <div className="modal-body" style={{maxHeight: "85vh"}}>
            {message && (
              <div className={`alert ${message.includes(t.missingFields) || message.includes(t.errorUpdate) ? 'alert-danger' : 'alert-success'}`}>
                {message}
              </div>
            )}
            <form onSubmit={handleSubmit}>
              <div className="row">
                <div className="col-md-6">
                  <div className="mb-3">
                    <label>{t.type}</label>
                    <div className="form-check">
                      <input
                        type="radio"
                        name="productType"
                        id="fertilizer"
                        className="form-check-input"
                        style={{ cursor: 'pointer' }}
                        checked={formData.type === 'FERTILIZER'}
                        onChange={() => setFormData({ ...formData, type: 'FERTILIZER', benefit: '', controlTarget: '' })}
                        required
                      />
                      <label className="form-check-label" style={{ cursor: 'pointer' }} htmlFor="fertilizer">
                        {t.fertilizer}
                      </label>
                    </div>
                    <div className="form-check">
                      <input
                        type="radio"
                        name="productType"
                        id="pesticide"
                        className="form-check-input"
                        style={{ cursor: 'pointer' }}
                        checked={formData.type === 'PESTICIDE'}
                        onChange={() => setFormData({ ...formData, type: 'PESTICIDE', benefit: '', controlTarget: '' })}
                        required
                      />
                      <label className="form-check-label" style={{ cursor: 'pointer' }} htmlFor="pesticide">
                        {t.pesticide}
                      </label>
                    </div>
                  </div>
                  <div className="mb-3">
                    <label>{t.name}</label>
                    <input
                      type="text"
                      className="form-control"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder={t.placeholderName}
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <label>{t.description}</label>
                    <textarea
                      className="form-control"
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      placeholder={t.placeholderDescription}
                      rows="3"
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <label>{t.storageMethod}</label>
                    <input
                      type="text"
                      className="form-control"
                      value={formData.storageMethod}
                      onChange={(e) => setFormData({ ...formData, storageMethod: e.target.value })}
                      placeholder={t.placeholderStorageMethod}
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <label>{t.formulation}</label>
                    <input
                      type="text"
                      className="form-control"
                      value={formData.formulation}
                      onChange={(e) => setFormData({ ...formData, formulation: e.target.value })}
                      placeholder={t.placeholderFormulation}
                      required
                    />
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="mb-3">
                    <label>{t.activeIngredient}</label>
                    <input
                      type="text"
                      className="form-control"
                      value={formData.activeIngredient}
                      onChange={(e) => setFormData({ ...formData, activeIngredient: e.target.value })}
                      placeholder={t.placeholderActiveIngredient}
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <label>{t.activeConcentration}</label>
                    <input
                      type="text"
                      className="form-control"
                      value={formData.activeConcentration}
                      onChange={(e) => setFormData({ ...formData, activeConcentration: e.target.value })}
                      placeholder={t.placeholderActiveConcentration}
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <label>{t.recommendedDose}</label>
                    <input
                      type="text"
                      className="form-control"
                      value={formData.recommendedDose}
                      onChange={(e) => setFormData({ ...formData, recommendedDose: e.target.value })}
                      placeholder={t.placeholderRecommendedDose}
                      required
                    />
                  </div>
                  <div className="mb-3">
                    <label>{t.applicationMethod}</label>
                    <input
                      type="text"
                      className="form-control"
                      value={formData.applicationMethod}
                      onChange={(e) => setFormData({ ...formData, applicationMethod: e.target.value })}
                      placeholder={t.placeholderApplicationMethod}
                      required
                    />
                  </div>
                  {formData.type === 'PESTICIDE' && (
                    <div className="mb-3">
                      <label>{t.controlTarget}</label>
                      <input
                        type="text"
                        className="form-control"
                        value={formData.controlTarget}
                        onChange={(e) => setFormData({ ...formData, controlTarget: e.target.value })}
                        placeholder={t.placeholderControlTarget}
                        disabled={formData.type !== 'PESTICIDE'}
                        required={formData.type === 'PESTICIDE'}
                      />
                    </div>
                  )}
                  {formData.type === 'FERTILIZER' && (
                    <div className="mb-3">
                      <label>{t.benefit}</label>
                      <textarea
                        className="form-control"
                        value={formData.benefit}
                        onChange={(e) => setFormData({ ...formData, benefit: e.target.value })}
                        placeholder={t.placeholderBenefit}
                        rows="3"
                        disabled={formData.type !== 'FERTILIZER'}
                        required={formData.type === 'FERTILIZER'}
                      />
                    </div>
                  )}
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={onClose}>
                  {t.cancelButton}
                </button>
                <button type="submit" className="btn btn-primary">
                  {isAddMode ? t.addButton : t.saveButton}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductModal;